import { apiClient } from './client';

// coordinator (TENANT-VIEWER-ROLE): a tenant staffer who runs the
// day-to-day (participants, sessions, procedure-instances, devices) but
// can't reshape the tenant — settings, member management, the
// procedure/baseline catalog, and billing stay tenant_admin-only.
export type Role =
  | 'superadmin'
  | 'tenant_admin'
  | 'coordinator'
  | 'participant';

export interface MembershipSummary {
  tenant_id: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
  role: Role;
  // False = the row exists for history but the role grant is
  // suspended. userHasRole() in AuthContext skips inactive rows.
  is_active: boolean;
  // ROADMAP #37: False = the tenant this membership points at has
  // been suspended. The backend's role gate treats this as a hard
  // deny on every tenant-scoped route — the portal renders an
  // explicit banner so the user sees why their tenant data is
  // gone instead of staring at silent 403s. Null on superadmin
  // rows (no tenant to suspend). Optional in the type so older
  // fixtures + (theoretically) older API responses default to
  // "active" rather than failing the gate.
  tenant_is_active?: boolean | null;
  // True only on a read-only tenant_admin grant: the holder sees all
  // of the tenant's data but can't mutate it. canWriteTenant() in
  // AuthContext consults this to hide/disable write controls; the
  // backend rejects writes with 403 as the hard safety net. Optional
  // so older fixtures/responses default to write-capable.
  read_only?: boolean;
  // The tenant's hidden-metric keys. Client-computed participant metric
  // surfaces (the "Your performance" grid) filter these out so they match
  // the server-filtered scorecard. Optional so older fixtures/responses
  // default to "nothing hidden".
  scorecard_hidden_metrics?: string[];
  // This tenant's time-display setting (TIMEZONE-DISPLAY): an IANA zone (e.g.
  // "UTC") or null = viewer-local. The app-root provider renders a tenant's
  // data in ITS zone by looking this up for the active tenant. Optional so
  // older fixtures/responses default to viewer-local.
  display_timezone?: string | null;
}

export interface UserContact {
  phone: string | null;
  mobile: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state_region: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface CurrentUser extends UserContact {
  id: string;
  email: string;
  full_name: string | null;
  // 10-digit National Provider Identifier; null when the user has
  // not entered one. Populated by the Profile page's NPI lookup.
  npi: string | null;
  // Professional details — required on the Profile page when the
  // user holds a participant role (UI gate). Nullable on the
  // server so older accounts + non-participant roles aren't broken.
  title: string | null;
  years_of_experience: number | null;
  email_verified_at: string | null;
  created_at: string;
  // TOTP 2FA enabled timestamp; null = not enabled. The Profile
  // page renders Enable vs Disable based on it.
  totp_enabled_at: string | null;
  // ROADMAP #18 phase 2: server-computed. True when the
  // org/platform policy says this user must have TOTP enrolled —
  // RequireAuth routes to /setup-2fa when this is True and
  // totp_enabled_at is null, and Profile hides the individual
  // Disable button when this is True (the policy is the source of
  // truth, not the per-user toggle). Derivation rules:
  //   - active superadmin + platform mfa_policy.totp_required → True
  //   - any active tenant_admin membership where the tenant's
  //     mfa_required is True → True
  //   - otherwise False (participants opt in individually)
  totp_required: boolean;
  // Active-tenant time-display setting (TIMEZONE-DISPLAY): an IANA zone (e.g.
  // "UTC") when the user has a single active tenant that sets one, else null =
  // the viewer's local browser zone. Feeds the app-root DisplayTimeZoneProvider.
  display_timezone: string | null;
  memberships: MembershipSummary[];
}

// PATCH body — every field optional; only ones present get written.
// Empty string explicitly clears the field on the server.
export type UserUpdate = Partial<{
  full_name: string;
  npi: string;
  title: string;
  years_of_experience: number | null;
  phone: string;
  mobile: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state_region: string;
  postal_code: string;
  country: string;
}>;

export async function getMe(): Promise<CurrentUser> {
  const { data } = await apiClient.get<CurrentUser>('/api/v1/users/me');
  return data;
}

export async function patchMe(body: UserUpdate): Promise<CurrentUser> {
  const { data } = await apiClient.patch<CurrentUser>('/api/v1/users/me', body);
  return data;
}
